import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import clsx from 'clsx'
import Avatar from 'react-avatar'
import { Link } from 'react-router-dom'

import { TApi, TRoutes } from '../../../../shared/const'
import { formatEffort } from '../../../../shared/effort'
import { alsText, hatInhalt } from '../../../../shared/richText'
import { stateLabel } from '../../../../shared/helpers'
import { formatDateTime as formatDateTimeInVienna } from '../../../../shared/datetime'
import http from '../../../../shared/http'
import useAuthImages from '../../../../shared/useAuthImages'
import { useAvatarUrls } from '../../../../shared/useAvatarUrls'
import { notifyError } from '../../../../shared/errorMessage'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'
import useDialogDismiss from '../../../../shared/useDialogDismiss'
import { askDiscardChanges } from '../../../../shared/confirmDialog'

import Btn from '../../../../UI/Btn'
import Loading from '../../../../UI/Loading'
import Lightbox from '../../../../UI/Lightbox'
import { RichTextView } from '../../../../UI/RichText'
import RoleChip from '../../../../UI/RoleChip'

import AttachmentLibrary from './AttachmentLibrary'
import CommentComposer from './CommentComposer'
import TicketFields from './TicketFields'
import laufzeitText from './projectRuntime'
import styles from './detail.module.scss'

const formatDate = value => formatDateTimeInVienna(value, { dateStyle: 'medium', timeStyle: 'short' })

// Statuswechsel und Zuweisungen werden als Kommentar geschrieben, dessen Text
// mit "Status:" bzw. "Zuweisung:" beginnt (siehe Backend, tickets/controller).
// Hier werden sie daran erkannt und als Ereignis dargestellt: fett die Art der
// Änderung, darunter das Vorher/Nachher.
const VERLAUFS_EINTRAG = /^(Status|Zuweisung):\s*([\s\S]*)$/

const TABS = [
	{ key: 'anforderung', label: 'Anforderung' },
	{ key: 'anhaenge', label: 'Anhänge & Screenshots' }
]

// Ab wie vielen Zeichen ein Beitrag in der Blase gekürzt wird. 250 Zeichen sind
// gut vier Zeilen — genug, um zu erkennen, worum es geht, und kurz genug, dass
// zehn Beiträge noch als Abfolge lesbar bleiben statt als Textwand.
const BUBBLE_MAX = 250

// Schneidet an der letzten Wortgrenze ab. Liegt keine in Reichweite (ein langer
// Pfad, eine Adresse), bleibt der harte Schnitt — sonst verlöre die Vorschau
// unter Umständen die halbe erlaubte Länge.
const kuerzen = text => {
	const roh = text.slice(0, BUBBLE_MAX)
	const luecke = roh.lastIndexOf(' ')
	return (luecke > BUBBLE_MAX - 40 ? roh.slice(0, luecke) : roh).trimEnd()
}

/**
 * Der Wortlaut eines Beitrags, bei Überlänge zusammengeklappt.
 *
 * Aufgeklappt wird an Ort und Stelle, nicht in einem eigenen Fenster: Der
 * lange Beitrag steht dann zwischen denselben Nachbarn wie vorher, und die
 * Abfolge bleibt lesbar. „Mehr lesen" klappt wieder zu — wer sich verlesen
 * hat, kommt ohne Umweg zurück zur Übersicht.
 */
function Kommentartext({ text }) {
	const [offen, setOffen] = useState(false)
	const inhalt = text || ''

	/*
	 * Gemessen wird am **reinen** Text, angezeigt wird der ausgezeichnete: Ein
	 * Beitrag mit drei fett gesetzten Wörtern trägt gut fünfzig Zeichen
	 * Auszeichnung, die niemand sieht — an der Zeichenzahl gemessen wäre er
	 * gekürzt, obwohl er kurz ist.
	 *
	 * Gekürzt wird deshalb auf reinen Text. Die Auszeichnung mitten im Satz
	 * abzuschneiden hieße, die offenen Tags von Hand schließen zu müssen; der
	 * Nutzen davon ist ein bisschen Fettdruck in einer Vorschau.
	 */
	const klar = alsText(inhalt)

	if (klar.length <= BUBBLE_MAX) return <RichTextView className={styles.timelineText} value={inhalt} />

	if (offen) {
		return (
			<>
				<RichTextView className={styles.timelineText} value={inhalt} />
				<button type="button" className={styles.moreBtn} onClick={() => setOffen(false)}>
					Weniger anzeigen
				</button>
			</>
		)
	}

	return (
		<>
			<p className={styles.timelineText}>{`${kuerzen(klar)} …`}</p>
			<button type="button" className={styles.moreBtn} onClick={() => setOffen(true)}>
				Mehr lesen
			</button>
		</>
	)
}

Kommentartext.propTypes = {
	text: PropTypes.string
}

Kommentartext.defaultProps = {
	text: ''
}

/**
 * Die Belege an einem Kommentar.
 *
 * Bilder als kleine Vorschau — sie öffnen dieselbe Vergrößerung wie die Anhänge
 * der Anforderung, deshalb bekommt die Leiste die gemeinsame Bildnummer
 * (`bildIndex`) und nicht ihre eigene Zählung. Alles andere (PDF) ist ein
 * Verweis zum Herunterladen: Eine Vorschau davon im Verlauf einzubauen wäre
 * viel Aufwand für einen Blick, den der Browser besser zeigt.
 */
function Anhangleiste({ anhaenge, urls, bildIndex, onOpen }) {
	return (
		<ul className={styles.msgAttachments}>
			{anhaenge.map(shot => {
				const url = urls[shot.id]
				const name = shot.caption || shot.originalName || 'Anhang'
				const istBild = (shot.mimeType || '').startsWith('image/')

				if (istBild) {
					return (
						<li key={shot.id} className={styles.msgAttachment}>
							{url ? (
								<button
									type="button"
									className={styles.msgThumbBtn}
									title={name}
									onClick={() => onOpen(bildIndex[shot.id])}
								>
									<img className={styles.msgThumb} src={url} alt={name} />
								</button>
							) : (
								<span className={styles.msgThumbLoading}>lädt …</span>
							)}
						</li>
					)
				}

				return (
					<li key={shot.id} className={styles.msgAttachment}>
						<a
							className={styles.msgFile}
							href={url}
							download={name}
							title={url ? 'Herunterladen' : 'lädt …'}
						>
							<span className={styles.msgFileBadge} aria-hidden="true">
								PDF
							</span>
							<span className={styles.msgFileName}>{name}</span>
						</a>
					</li>
				)
			})}
		</ul>
	)
}

Anhangleiste.propTypes = {
	anhaenge: PropTypes.arrayOf(PropTypes.object).isRequired,
	urls: PropTypes.object.isRequired,
	bildIndex: PropTypes.object.isRequired,
	onOpen: PropTypes.func.isRequired
}

/**
 * Detailansicht einer Anforderung.
 *
 * **Zwei Spalten.** Links der Inhalt mit seinen zwei Reitern, rechts die
 * Einordnung: Art, Status, Zuweisung, Dringlichkeit, Wichtigkeit, Projekt. Die
 * sechs lagen bis 22.08.2026 an zwei Orten — drei über den Reitern, drei in
 * einem Reiter „Einstufung" —, obwohl sie zusammengehören; wer die
 * Dringlichkeit ändern wollte, verließ dafür den Text, um den es ging. Jetzt
 * stehen sie immer daneben und scrollen nicht mit, und der Reiter „Einstufung"
 * ist entfallen.
 *
 * An einen Beitrag dürfen bis zu fünf Belege (PNG, JPEG, PDF). Sie hängen an
 * ihm, nicht in einem gemeinsamen Stapel — dort stünde das Bild ohne den Satz,
 * der es erklärt. Der Reiter „Anhänge & Screenshots" zeigt sie trotzdem alle:
 * Er ist seit 22.08.2026 ein **Archiv und keine Ablagefläche** (siehe
 * `AttachmentLibrary`). Hochgeladen wird am Kommentarfeld, wo auch die
 * Beschreibung der Datei entsteht.
 *
 * „Bearbeiten" und „Löschen" sieht nur, wer die Anforderung erfasst hat. Wer
 * fremde Anforderungen bearbeitete, änderte fremde Worte; die Einstufung
 * (Status, Zuweisung, Dringlichkeit) bleibt für alle offen — sie ist die
 * gemeinsame Arbeit an der Anforderung, nicht ihr Inhalt.
 *
 * Der Reiter „Anforderung" trägt den ganzen Ablauf: Beschreibung, Eingabefeld
 * und darunter **eine** Zeitleiste aus den Beiträgen der Nutzer:innen und den
 * automatisch mitgeschriebenen Ereignissen (Anlage, Status- und
 * Zuweisungswechsel). Ein eigener Reiter „Kommentare" zwang dazu, zwischen der
 * Anforderung und der Antwort darauf hin- und herzuspringen — beides gehört
 * gelesen, um zu verstehen, wo die Sache steht.
 *
 * Beitrag und Ereignis unterscheiden sich in der Form, nicht in der Spur: Was
 * jemand geschrieben hat, steht in einer Sprechblase, was das System
 * mitschrieb, steht als schmale Zeile daneben. Lange Beiträge sind auf 250
 * Zeichen gekürzt und mit „Mehr lesen" aufzuklappen, damit eine einzelne
 * Rückfrage über zwei Bildschirmseiten nicht den Ablauf verdeckt.
 *
 * Beschreibung und Beiträge tragen die vier Grundauszeichnungen (fett, kursiv,
 * unterstrichen, Aufzählung) — angezeigt über `UI/RichText`, das den alten,
 * reinen Text unverändert weiter darstellt.
 *
 * Zwei Arten von Blättern liegen hier übereinander: zwischen den Bildern eines
 * Tickets und zwischen den Tickets selbst. Die Reihenfolge der Tickets kommt
 * von der Liste — was gefiltert und sortiert wurde, gilt auch hier.
 */
export default function TicketDetail(props) {
	const { ticketId, siblings, onNavigate, onClose, onChanged } = props

	const [ticket, setTicket] = useState(null)
	const [activeTab, setActiveTab] = useState('anforderung')
	const [lightboxIndex, setLightboxIndex] = useState(null)
	const [comment, setComment] = useState('')
	// Die Belege zum noch nicht abgeschickten Beitrag. Sie gehen erst mit dem
	// „Senden" an den Server — vorher gibt es den Kommentar nicht, an dem sie
	// hängen könnten.
	const [commentFiles, setCommentFiles] = useState([])
	const [isPosting, setPosting] = useState(false)

	const tabsRef = useRef(null)
	useScrollTopOnTabChange(activeTab, tabsRef)

	/*
	 * „Bearbeiten" und „Löschen" gehören der Person, die die Anforderung erfasst
	 * hat. Für alle anderen sind die Knöpfe nicht abgeblendet, sondern gar nicht
	 * da — ein Knopf, der nur beim Drücken sagt, dass er nicht darf, ist eine
	 * Enttäuschung mehr als ein Hinweis.
	 *
	 * Ist niemand hinterlegt (gelöschtes Konto), zeigt das Fenster die beiden
	 * Knöpfe niemandem. Das ist der vorsichtige Fall: Es gibt dann keine Person,
	 * der die Anforderung gehört.
	 */
	const eigeneId = useSelector(state => state.user.profile.id)
	const istAutor = Boolean(eigeneId && ticket?.createdBy?.id === eigeneId)

	const position = siblings.indexOf(ticketId)
	const hasPrev = position > 0
	const hasNext = position >= 0 && position < siblings.length - 1

	const load = useCallback(() => {
		setTicket(null)
		http.get(TApi.ADMIN_TICKET_ID(ticketId), { disableToast: true })
			.then(({ data }) => setTicket(data))
			.catch(error => {
				notifyError(error, 'Die Anforderung konnte nicht geladen werden.')
				onClose()
			})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticketId])

	useEffect(() => {
		load()
	}, [load])

	// Beim Wechsel auf ein anderes Ticket wieder mit der Anforderung beginnen und
	// die vergrößerte Ansicht schließen — sonst zeigt sie kurz die Bilder des
	// vorigen Tickets.
	useEffect(() => {
		setActiveTab('anforderung')
		setLightboxIndex(null)
	}, [ticketId])

	/*
	 * Alle Dateien der Anforderung: der Stapel im Reiter „Anhänge" und die
	 * Belege an den Kommentaren. Sie liegen am selben Ort und werden über
	 * dieselbe angemeldete Route ausgeliefert — eine Liste genügt, und die
	 * Vorschau blättert dann über beides.
	 */
	const anhaenge = useMemo(() => {
		if (!ticket) return []

		const ausKommentaren = (ticket.comments || []).flatMap(entry => entry.attachments || [])
		return [...(ticket.screenshots || []), ...ausKommentaren]
	}, [ticket])

	const sources = useMemo(
		() =>
			anhaenge.map(shot => ({
				id: shot.id,
				url: TApi.ADMIN_TICKET_SCREENSHOT_FILE(ticketId, shot.id)
			})),
		[anhaenge, ticketId]
	)
	const imageUrls = useAuthImages(sources)

	// Nur Bilder lassen sich vergrößern; ein PDF wird heruntergeladen. Der
	// Platz in dieser Liste ist zugleich die Nummer, mit der die Vorschau
	// aufgeht — deshalb daneben die Zuordnung Kennung → Platz.
	const images = useMemo(
		() =>
			anhaenge
				.filter(shot => imageUrls[shot.id])
				.map(shot => ({ src: imageUrls[shot.id], caption: shot.caption || shot.originalName })),
		[anhaenge, imageUrls]
	)

	const bildIndex = useMemo(() => {
		const map = {}
		anhaenge
			.filter(shot => imageUrls[shot.id])
			.forEach((shot, index) => {
				map[shot.id] = index
			})
		return map
	}, [anhaenge, imageUrls])

	/*
	 * Eine Zeitleiste aus allem, was zu dieser Anforderung geschehen ist:
	 * Kommentare und automatische Ereignisse liegen im Backend ohnehin im
	 * selben Feld. Das Neueste zuerst — beim Öffnen interessiert der letzte
	 * Stand, nicht die Anlage vor drei Wochen.
	 */
	const timeline = useMemo(() => {
		if (!ticket) return []

		const eintraege = (ticket.comments || []).map(entry => {
			const ereignis = VERLAUFS_EINTRAG.exec(entry.body || '')
			return {
				id: entry.id,
				createdAt: entry.createdAt,
				istKommentar: !ereignis,
				titel: ereignis ? `${ereignis[1]} geändert` : 'Kommentar',
				text: ereignis ? ereignis[2] : entry.body,
				attachments: entry.attachments || [],
				author: entry.author,
				// Rolle der schreibenden Person: In diesem Verlauf stehen die
				// meldende Person und die Verwaltung untereinander.
				rolle: entry.authorRole || null,
				avatarHashName: entry.authorAvatarHashName || null,
				// Ging der Beitrag an die meldende Person? Das muss am Beitrag
				// stehen: Wer antwortet, soll sehen, was hinausgegangen ist —
				// und was nur hier steht.
				sichtbar: entry.visibility === 'REPORTER'
			}
		})

		// Die Anlage gehört ans Ende der Zeitleiste — sie ist kein Kommentar,
		// sondern das erste Ereignis der Anforderung.
		eintraege.push({
			id: 'erstellt',
			createdAt: ticket.createdAt,
			istKommentar: false,
			titel: 'Erstellt',
			text: '',
			attachments: [],
			author: ticket.createdBy?.name,
			rolle: ticket.createdBy?.role || null,
			avatarHashName: ticket.createdBy?.avatarHashName || null
		})

		return eintraege.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
	}, [ticket])

	/*
	 * Die Profilbilder aller Beteiligten in einem Zug. Ein Bild wird angemeldet
	 * geholt und als lokale Adresse eingesetzt — ein `<img src>` auf die
	 * Schnittstelle schickt keinen Anmelde-Kopfeintrag mit. Wer keines hat oder
	 * dessen Konto es nicht mehr gibt, bleibt bei den Initialen.
	 */
	const avatarUrls = useAvatarUrls(useMemo(() => timeline.map(entry => entry.avatarHashName), [timeline]))

	// Eine Stelle fuer alle Aenderungen aus der Kopfzeile - Status,
	// Dringlichkeit, Wichtigkeit. Sonst dreimal derselbe Aufruf.
	const patch = felder => {
		// `disableToast`: Der eigene Satz benennt den Vorgang, der schiefging.
		http.put(TApi.ADMIN_TICKET_ID(ticketId), felder, { disableToast: true })
			.then(({ data }) => {
				setTicket(data)
				onChanged()
			})
			.catch(error => notifyError(error, 'Die Änderung konnte nicht gespeichert werden.'))
	}

	/**
	 * Schickt den Beitrag ab — und danach seine Belege.
	 *
	 * Zwei Schritte, weil die Datei die Kennung des Kommentars braucht, den es
	 * vor dem Speichern noch nicht gibt. Nacheinander statt parallel: Die
	 * Reihenfolge entsteht am Server aus der Position des zuletzt gespeicherten
	 * Anhangs.
	 *
	 * Geht ein Anhang schief, bleibt der Kommentar trotzdem stehen — der Satz
	 * ist das Wichtigere. Es wird nur gesagt, was fehlt.
	 */
	const postComment = async (sichtbarkeit = {}) => {
		// `hatInhalt` statt `trim()`: Ein leer geräumtes Eingabefeld hinterlässt
		// `<p><br></p>` — für den Browser Inhalt, für die Leserin nichts.
		if (!hatInhalt(comment)) return
		const text = comment.trim()

		setPosting(true)
		try {
			// `disableToast`: Der eigene Satz benennt den Vorgang, der schiefging.
			const { data } = await http.post(
				TApi.ADMIN_TICKET_COMMENTS(ticketId),
				{ body: text, ...sichtbarkeit },
				{ disableToast: true }
			)

			let fehler = 0
			for (const { file, description } of commentFiles) {
				const body = new FormData()
				body.append('screenshot', file, file.name || 'anhang')
				body.append('commentId', String(data.id))
				// Der Satz zur Datei geht mit ihr hinaus — ein zweiter Aufruf
				// hinterher wäre eine weitere Stelle, an der er verlorengehen kann.
				if (description?.trim()) body.append('description', description.trim())
				// eslint-disable-next-line no-await-in-loop
				await http
					.post(TApi.ADMIN_TICKET_SCREENSHOTS(ticketId), body, { disableToast: true })
					.catch(() => {
						fehler += 1
					})
			}

			if (fehler) {
				toast.warn(
					`${fehler} von ${commentFiles.length} Anhängen konnten nicht gespeichert werden.`
				)
			}

			setComment('')
			setCommentFiles([])
			load()
			onChanged()
		} catch (error) {
			notifyError(error, 'Der Kommentar konnte nicht gespeichert werden.')
		} finally {
			setPosting(false)
		}
	}

	/*
	 * Schließen mit Rückfrage. Geändertes wird hier sofort gespeichert — offen
	 * ist nur ein angefangener Kommentar samt seiner Anhänge. Der ginge sonst
	 * still verloren.
	 */
	const schliessen = async () => {
		if ((hatInhalt(comment) || commentFiles.length) && !(await askDiscardChanges())) return

		onClose()
	}

	const backdropProps = useDialogDismiss(schliessen, { enabled: lightboxIndex === null })

	const remove = () => {
		// eslint-disable-next-line no-alert
		if (!window.confirm(`Anforderung #${ticketId} wirklich löschen? Das lässt sich nicht rückgängig machen.`))
			return

		http.delete(TApi.ADMIN_TICKET_ID(ticketId), { disableToast: true })
			.then(() => {
				toast.success('Anforderung gelöscht.')
				onClose()
				onChanged()
			})
			.catch(error => notifyError(error, 'Die Anforderung konnte nicht gelöscht werden.'))
	}

	// Die Zahl am Reiter zaehlt jetzt alles, was der Reiter zeigt — auch die
	// Belege an den Beitraegen. Sonst stuende dort „0", waehrend darunter fuenf
	// Bilder liegen.
	const tabCount = key => (key === 'anhaenge' ? anhaenge.length : null)

	return (
		<div className={styles.backdrop} {...backdropProps}>
			<div className={styles.modal}>
				<header className={styles.head}>
					<div className={styles.headLeft}>
						<button
							type="button"
							className={styles.navBtn}
							disabled={!hasPrev}
							title="Vorherige Anforderung"
							onClick={() => onNavigate(siblings[position - 1])}
						>
							‹
						</button>
						<span className={styles.position}>
							{position >= 0 ? `${position + 1} von ${siblings.length}` : ''}
						</span>
						<button
							type="button"
							className={styles.navBtn}
							disabled={!hasNext}
							title="Nächste Anforderung"
							onClick={() => onNavigate(siblings[position + 1])}
						>
							›
						</button>
						{/*
						 * Nummer und Titel dort, wo eine Fensterbeschriftung
						 * hingehört. Bei Überlänge gekürzt — vollständig steht der
						 * Titel im Reiter „Anforderung" und im Tooltip.
						 */}
						{ticket && (
							<span className={styles.headTitle} title={`#${ticket.id} ${ticket.title}`}>
								#{ticket.id} {ticket.title}
							</span>
						)}
					</div>
					<button type="button" className={styles.close} onClick={schliessen} aria-label="Schließen">
						×
					</button>
				</header>

				{!ticket ? (
					<div className={styles.body}>
						<Loading />
					</div>
				) : (
					<div className={styles.split}>
					<div className={styles.body}>
						<div className={styles.tabs} role="tablist" ref={tabsRef}>
							{TABS.map(tab => {
								const count = tabCount(tab.key)
								return (
									<button
										key={tab.key}
										type="button"
										role="tab"
										aria-selected={activeTab === tab.key}
										className={clsx(styles.tab, activeTab === tab.key && styles.tabActive)}
										onClick={() => setActiveTab(tab.key)}
									>
										{tab.label}
										{count !== null && <span className={styles.tabCount}>{count}</span>}
									</button>
								)
							})}
						</div>

						<div className={styles.panel}>
							{activeTab === 'anforderung' && (
								<>
									{/*
									 * Titel, Beschreibung, Art — dieselbe Reihenfolge wie
									 * im Erfassungsfenster. Der Titel steht hier ganz; die
									 * Kopfzeile kürzt ihn.
									 */}
									<section className={styles.reqCard}>
										<h3 className={styles.sectionTitle}>Titel</h3>
										<p className={styles.title}>{ticket.title}</p>

										<h3 className={styles.sectionTitleSpaced}>Beschreibung</h3>
										{ticket.description ? (
											<RichTextView
												className={styles.description}
												value={ticket.description}
											/>
										) : (
											<p className={styles.emptyNote}>
												Keine Beschreibung hinterlegt. Über „Bearbeiten"
												lassen sich Titel und Beschreibung ändern.
											</p>
										)}

										{/*
										 * Wer das geschrieben hat und wann — leise unter dem
										 * Text, wie die Zeile unter einem Beitrag im Verlauf.
										 * Der erfasste Wortlaut ist damit sichtbar jemandes
										 * Beitrag und nicht anonymer Inhalt des Fensters; die
										 * Kommentare darunter tragen dieselbe Zeile.
										 */}
										<p className={styles.byline}>
											{/*
											 * Über den Hilfe-Knopf gemeldet oder intern
											 * erfasst? Das steht hier und nicht in der
											 * Einordnung: Wer den Text liest, will sofort
											 * wissen, ob dahinter jemand wartet.
											 */}
											{ticket.reporter ? (
												<>
													Gemeldet {formatDate(ticket.createdAt)} von{' '}
													{ticket.reporter.name}
													{/*
													 * Die Rolle beim Melden geht vor der Rolle
													 * am Konto — sie beschreibt, aus welcher
													 * Ecke die Meldung kam.
													 */}
													<RoleChip
														role={ticket.context?.role || ticket.reporter.role}
														className={styles.bylineRolle}
													/>
													{ticket.responsibleState
														? ` · ${stateLabel(ticket.responsibleState)}`
														: ' · bundesweit'}
													{ticket.context?.path && ` · ${ticket.context.path}`}
												</>
											) : (
												<>
													Erfasst {formatDate(ticket.createdAt)}
													{ticket.createdBy?.name && ` von ${ticket.createdBy.name}`}
													<RoleChip
														role={ticket.createdBy?.role}
														className={styles.bylineRolle}
													/>
												</>
											)}
											{ticket.updatedAt !== ticket.createdAt &&
												` · geändert ${formatDate(ticket.updatedAt)}`}
										</p>
									</section>

									{/*
									 * Kommentieren, ohne den Reiter zu wechseln: Wer die
									 * Beschreibung gerade gelesen hat, hat seine Rückfrage
									 * jetzt im Kopf — und nicht mehr, nachdem er sie einen
									 * Reiter weiter noch einmal suchen musste. Das Feld
									 * steht über der Zeitleiste, nicht unter ihr: Schreiben
									 * soll nicht erst ans Ende von zwanzig Einträgen
									 * scrollen lassen.
									 */}
									<CommentComposer
										value={comment}
										onChange={setComment}
										// Nur wo es eine meldende Person gibt, gibt es
										// etwas sichtbar zu machen.
										hasReporter={Boolean(ticket?.reporter)}
										files={commentFiles}
										// Datei und ihr Satz wandern zusammen durch die
										// Warteschlange — getrennt gefuehrt liefen die
										// beiden Listen beim Entfernen auseinander.
										onFiles={neue =>
											setCommentFiles(bisher => [
												...bisher,
												...neue.map(file => ({ file, description: '' }))
											])
										}
										onRemoveFile={index =>
											setCommentFiles(bisher => bisher.filter((_, i) => i !== index))
										}
										onDescribeFile={(index, text) =>
											setCommentFiles(bisher =>
												bisher.map((eintrag, i) =>
													i === index ? { ...eintrag, description: text } : eintrag
												)
											)
										}
										onSend={postComment}
										isPosting={isPosting}
									/>

									<h3 className={styles.timelineHeading}>
										Verlauf
										<span className={styles.tabCount}>{timeline.length}</span>
									</h3>

									<ol className={styles.timeline}>
										{timeline.map(entry => (
											<li key={entry.id} className={styles.timelineItem}>
												{/*
												 * Am Beitrag steht das Gesicht, am Ereignis der
												 * Punkt: Einen Kommentar hat jemand geschrieben,
												 * ein Statuswechsel ist bloß geschehen. Wer den
												 * Verlauf überfliegt, sieht damit ohne zu lesen,
												 * wer wie oft zu Wort kam.
												 */}
												{entry.istKommentar ? (
													<Avatar
														className={styles.timelineAvatar}
														src={avatarUrls[entry.avatarHashName] || undefined}
														name={entry.author || '?'}
														color="#e5eaf5"
														fgColor="#22404d"
														size="30"
														round
													/>
												) : (
													<span
														className={clsx(styles.timelineDot, styles.timelineDotEvent)}
													/>
												)}
												<div className={styles.timelineBody}>
													{/*
													 * Ein Beitrag steht in einer Blase — was
													 * jemand geschrieben hat, ist damit auf den
													 * ersten Blick von dem zu unterscheiden, was
													 * das System mitschrieb. Ereignisse (Status,
													 * Zuweisung) bleiben eine schmale Zeile: Sie
													 * sagen im Kopf, was geschah, und tragen den
													 * Wechsel darunter.
													 */}
													{entry.istKommentar ? (
														<div className={styles.bubble}>
															<Kommentartext text={entry.text} />
															{Boolean(entry.attachments.length) && (
																<Anhangleiste
																	anhaenge={entry.attachments}
																	urls={imageUrls}
																	bildIndex={bildIndex}
																	onOpen={setLightboxIndex}
																/>
															)}
															<p className={styles.byline}>
																{formatDate(entry.createdAt)}
																{entry.author ? ` von ${entry.author}` : ''}
																<RoleChip
																	role={entry.rolle}
																	className={styles.bylineRolle}
																/>
																{entry.sichtbar
																	? ' · für die meldende Person sichtbar'
																	: ' · intern'}
															</p>
														</div>
													) : (
														<>
															<div className={styles.timelineHead}>
																<span className={styles.timelineTitle}>
																	{entry.titel}
																</span>
																<span className={styles.timelineMeta}>
																	{formatDate(entry.createdAt)}
																	{entry.author ? ` · ${entry.author}` : ''}
																	<RoleChip
																		role={entry.rolle}
																		className={styles.bylineRolle}
																	/>
																</span>
															</div>
															{entry.text && (
																<p className={styles.timelineText}>{entry.text}</p>
															)}
														</>
													)}
												</div>
											</li>
										))}
									</ol>
								</>
							)}

							{activeTab === 'anhaenge' && (
								<>
									<div className={styles.shotsHead}>
										<h3 className={styles.sectionTitle}>Alle Dateien dieser Anforderung</h3>
										{Boolean(anhaenge.length) && (
											<span className={styles.shotsHint}>
												Klicken zum Vergrößern · ←/→ blättert · herunterladen
											</span>
										)}
									</div>

									{/*
									 * Ein Archiv, keine Ablagefläche: Neue Dateien kommen
									 * am Kommentarfeld herein, wo auch der Satz dazu
									 * geschrieben wird. Hier stehen sie alle beieinander
									 * — die des Stapels und die an den Beiträgen.
									 */}
									<AttachmentLibrary
										dateien={anhaenge}
										urls={imageUrls}
										bildIndex={bildIndex}
										onOpen={setLightboxIndex}
									/>
								</>
							)}
						</div>
					</div>

					{/*
					 * Die Einordnung als Spalte rechts, über allen Reitern.
					 *
					 * Vorher lag sie zur Hälfte über den Reitern (Art, Status,
					 * Zuweisung) und zur Hälfte in einem eigenen Reiter
					 * („Einstufung": Dringlichkeit, Wichtigkeit, Projekt) — sechs
					 * Angaben, die zusammengehören, an zwei Orten. Wer die
					 * Dringlichkeit ändern wollte, verließ dafür den Text, um den
					 * es ging. Jetzt stehen alle sechs immer daneben, egal welcher
					 * Reiter offen ist, und der Reiter „Einstufung" ist entfallen.
					 */}
					<aside className={styles.sidebar}>
						<h3 className={styles.sidebarTitle}>Einordnung</h3>

						<TicketFields
							spalte
							values={{
								type: ticket.type,
								status: ticket.status,
								assignedTo: ticket.assignedToId ?? null,
								urgency: ticket.urgency,
								importance: ticket.importance,
								projectId: ticket.projectId ?? null
							}}
							projects={props.projects}
							assignees={props.assignees}
							onChange={(feld, wert) => patch({ [feld]: wert })}
						/>

						<div className={styles.sidebarMeta}>
							<span className={styles.scoreChip} title="Dringlichkeit × Wichtigkeit">
								Score {ticket.score}
							</span>
							{laufzeitText(ticket.project) && (
								<span className={styles.meta}>Laufzeit {laufzeitText(ticket.project)}</span>
							)}
						</div>

						{/*
						 * Was an dieser Anforderung schon entwickelt wurde. Der
						 * Verweis öffnet die Entwicklungen gefiltert auf diese
						 * Nummer — dort führt die Nummer wieder hierher zurück.
						 */}
						<div className={styles.developments}>
							<span className={styles.developmentsLabel}>Entwicklungen</span>
							{ticket.developments?.count ? (
								<Link
									className={styles.developmentsLink}
									to={`${TRoutes.CHANGELOG}?f_ticketId=${ticket.id}`}
								>
									{ticket.developments.count}{' '}
									{ticket.developments.count === 1 ? 'Entwicklung' : 'Entwicklungen'} ·{' '}
									{formatEffort(ticket.developments.minutes)}
								</Link>
							) : (
								<span className={styles.meta}>Noch keine verknüpft</span>
							)}
						</div>
					</aside>
					</div>
				)}

				{/*
				 * Ausserhalb des scrollenden Teils: „Bearbeiten" und „Loeschen"
				 * muessen erreichbar sein, ohne erst ans Ende einer langen
				 * Beschreibung zu scrollen.
				 */}
				{ticket && istAutor && (
					<footer className={styles.foot}>
						<div className={styles.footActions}>
							<Btn size="small" theme="danger" onClick={remove}>
								Löschen
							</Btn>
							<Btn size="small" theme="primary" onClick={() => props.onEdit(ticket)}>
								Bearbeiten
							</Btn>
						</div>
					</footer>
				)}
			</div>

			{lightboxIndex !== null && images.length > 0 && (
				<Lightbox
					images={images}
					index={Math.min(lightboxIndex, images.length - 1)}
					onIndexChange={setLightboxIndex}
					onClose={() => setLightboxIndex(null)}
				/>
			)}
		</div>
	)
}

TicketDetail.propTypes = {
	ticketId: PropTypes.number.isRequired,
	siblings: PropTypes.arrayOf(PropTypes.number).isRequired,
	projects: PropTypes.array,
	assignees: PropTypes.array,
	onNavigate: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
	onChanged: PropTypes.func.isRequired,
	onEdit: PropTypes.func.isRequired
}

TicketDetail.defaultProps = {
	projects: [],
	assignees: []
}
